import MoneyUtils from '../../services/MoneyUtils';
import Auth from '../../services/Auth';
import BitsyApollo from '../../services/BitsyApollo';
import BBAnalytics from '../../services/BBAnalytics';
import Content from '../../constants/Content';
import CartUtils from '../../services/CartUtils';
import Logger from '../../services/Logger';
import Environment from '../../services/Environment';
import Features from '../../services/Features';
import generalHelpers from '../../services/generalHelpers';

function getAppropriateShippingOption(
  allShippingOptions,
  shippingRegion,
  selectedShippingOptionId,
) {
  const selectedShippingOption = allShippingOptions.find(
    (shippingOption) =>
      shippingOption._id.toString() === selectedShippingOptionId,
  );
  const internationalShippingOption = allShippingOptions.find(
    (shippingOption) => shippingOption.isInternational,
  );
  const oconusShippingOption = allShippingOptions.find(
    (shippingOption) => shippingOption.shippingRegion === 'OCONUS',
  );
  if (selectedShippingOption) {
    if (
      shippingRegion === 'ROW' &&
      !selectedShippingOption.isInternational &&
      internationalShippingOption
    ) {
      return internationalShippingOption._id.toString();
    }
    if (
      shippingRegion === 'OCONUS' &&
      oconusShippingOption &&
      selectedShippingOption.shippingRegion !== 'OCONUS' &&
      selectedShippingOption.cost < 9.99
    ) {
      return oconusShippingOption._id.toString();
    }
  }
  return allShippingOptions[0]._id.toString();
}

function getDefaultExclusiveShipping(selectedShippingOptionId, cartInfo) {
  let resultShipping = selectedShippingOptionId;
  try {
    if (
      !selectedShippingOptionId &&
      !selectedShippingOptionId.trim() &&
      selectedShippingOptionId.trim().length === 0
    ) {
      if (
        cartInfo &&
        cartInfo.firstBoxShipping &&
        cartInfo.firstBoxShipping._id
      ) {
        resultShipping = cartInfo.firstBoxShipping._id;
      }
    }
  } catch (err) {
    Logger.warn('could not default shipping type');
    resultShipping = '';
  }
  return resultShipping;
}

function calculateOrderDerivedValues(
  cartInfo,
  allShippingOptions,
  selectedShippingOptionId,
  isBrownBoxSelected,
  billingValues,
  isBillingSameAsShipping,
  shippingValues,
  isRegistryCheckout,
  companyInfo,
) {
  let shippingOptions = allShippingOptions;
  let shippingCosts = cartInfo.firstBoxShipping
    ? cartInfo.firstBoxShipping.cost
    : 0;

  if (isRegistryCheckout) shippingCosts = 0;
  if (
    companyInfo &&
    companyInfo.shippingOptions &&
    companyInfo.shippingOptions.length
  ) {
    shippingOptions = companyInfo.shippingOptions;
  }
  if (selectedShippingOptionId) {
    const shippingOptionSelected = shippingOptions.find(
      (shippingOption) => shippingOption._id === selectedShippingOptionId,
    );
    if (shippingOptionSelected) {
      shippingCosts = shippingOptionSelected.cost;
    }
  }

  if (isBrownBoxSelected) {
    shippingCosts += 4.99;
  }

  const clonedSubCosts = { ...cartInfo.subCosts };

  if (isRegistryCheckout) {
    clonedSubCosts.shippingInsuranceCost = 0;
  }

  const hasSubscription = !!cartInfo.subscription;
  // If a subscription has an actualCostAsShipping value (such as a vitamin plan) the subTotal and shipping values need to be adjusted
  if (hasSubscription && cartInfo.subscription.plan.actualCostAsShipping) {
    clonedSubCosts.subTotal -= cartInfo.subscription.plan.actualCostAsShipping;
    shippingCosts += cartInfo.subscription.plan.actualCostAsShipping;
  }
  clonedSubCosts.shipping = shippingCosts;

  const totals = {
    taxesAndFees: MoneyUtils.calculateTaxesAndFees(clonedSubCosts),
    giftCardDiscount: MoneyUtils.calculateGiftCardDiscount(clonedSubCosts),
    subTotal: MoneyUtils.formatAsMoney(clonedSubCosts.subTotal),
    shipping: MoneyUtils.formatAsMoney(shippingCosts),
    couponDiscount: MoneyUtils.formatAsMoney(clonedSubCosts.couponDiscount),
    pointsDiscount: MoneyUtils.formatAsMoney(clonedSubCosts.pointsDiscount),
    shippingInsuranceCost: MoneyUtils.formatAsMoney(
      clonedSubCosts.shippingInsuranceCost,
    ),
    total: MoneyUtils.calculateTotal(clonedSubCosts),
  };

  let couponFreeShipping = false;
  if (cartInfo.coupon) couponFreeShipping = cartInfo.coupon.freeShipping;

  const { subTotal } = totals;
  const differenceForFreeShipping =
    49 -
    parseFloat(subTotal) +
    parseFloat(totals.couponDiscount || 0) +
    parseFloat(totals.pointsDiscount || 0);
  const hasFreeShipping =
    differenceForFreeShipping <= 0 ||
    couponFreeShipping ||
    cartInfo.isCartDigitalOnly === true ||
    (cartInfo.eligibleForReferral !== 'infinity' && hasSubscription);

  return {
    totals,
    couponFreeShipping,
    allShippingOptions,
    hasSubscription,
    differenceForFreeShipping,
    hasFreeShipping,
    isCartDigitalOnly: cartInfo.isCartDigitalOnly,
  };
}

// eslint-disable-next-line no-underscore-dangle
function _getCorporateShippingOptions(
  companyInfo,
  allShippingOptions,
  shippingRegion,
) {
  const filteredShippingOptions = [];
  let shippingOptions;
  // if corporate checkout and company has set shipping options
  if (companyInfo.shippingOptions.length) {
    shippingOptions = companyInfo.shippingOptions;
    // if corporate checkout and company does not have set shipping options, use free shipping option
  } else {
    const freeShippingOption = allShippingOptions.filter(
      (shippingOption) =>
        shippingOption.cost === 0 ||
        shippingOption.isInternational ||
        shippingOption.shippingRegion === 'OCONUS',
    );
    shippingOptions = freeShippingOption;
  }
  shippingOptions.forEach((shippingOption) => {
    if (
      (shippingRegion === 'CONUS' &&
        !shippingOption.isInternational &&
        shippingOption.shippingRegion !== 'OCONUS') ||
      (shippingRegion === 'ROW' && shippingOption.isInternational) ||
      (shippingRegion === 'OCONUS' &&
        shippingOption.shippingRegion === 'OCONUS')
    ) {
      filteredShippingOptions.push(shippingOption);
    }
  });
  return filteredShippingOptions;
}

function getFilteredShippingOptions(
  allShippingOptions,
  hasFreeShipping,
  shippingRegion,
  companyInfo,
) {
  const filteredShippingOptions = [];
  let shippingOptions;
  if (companyInfo && companyInfo.shippingOptions) {
    shippingOptions = _getCorporateShippingOptions(
      companyInfo,
      allShippingOptions,
      shippingRegion,
    );
    return shippingOptions;
  }
  shippingOptions = allShippingOptions;

  shippingOptions.forEach((shippingOption) => {
    if (shippingRegion !== 'ROW' && !shippingOption.isInternational) {
      const shippingOptionIsNotOconus =
        shippingOption.shippingRegion !== 'OCONUS';
      const cartShippingRegionIsNotOconus = shippingRegion !== 'OCONUS';
      const shippingOptionIsFree = shippingOption.cost <= 0;
      const cartAndShippingOptionRegionsMatch =
        shippingOption.shippingRegion === shippingRegion;

      if (
        (!shippingOptionIsFree && shippingOptionIsNotOconus) ||
        (cartShippingRegionIsNotOconus &&
          shippingOptionIsFree &&
          hasFreeShipping) ||
        cartAndShippingOptionRegionsMatch
      ) {
        filteredShippingOptions.push(shippingOption);
      }
    } else if (shippingRegion === 'ROW' && shippingOption.isInternational) {
      filteredShippingOptions.push(shippingOption);
    }
  });

  return filteredShippingOptions;
}

async function getAvailablePoints() {
  if (!Auth.isLoggedIn()) {
    return 0;
  }

  const { data } = await BitsyApollo.availablePointsQuery();
  const { availablePoints } = data;
  let points = 0;
  if (availablePoints && availablePoints.availablePoints) {
    points = availablePoints.availablePoints;
  }
  return points;
}

async function calculateApplicablePoints(cartInfo, totals) {
  let subTotal = 0;
  const couponDiscount = parseInt(totals.couponDiscount, 10);
  cartInfo.productsAndDetails.forEach((productAndDetailsItem) => {
    subTotal +=
      productAndDetailsItem.amountToCharge * productAndDetailsItem.quantity;
  });
  subTotal -= couponDiscount;
  const totalAvailablePoints = await getAvailablePoints();
  const totalAvailableDollars = Math.floor(totalAvailablePoints / 10);
  if (totalAvailableDollars === 0 || subTotal < 1) return 0;

  let maximumSubTotalPointsCanCover = Math.floor(subTotal / 2);
  if (maximumSubTotalPointsCanCover > 10) maximumSubTotalPointsCanCover = 10;
  if (totalAvailableDollars > maximumSubTotalPointsCanCover) {
    return maximumSubTotalPointsCanCover * 10;
  }
  return totalAvailableDollars * 10;
}

function updateGuestCheckoutFormValidity(state) {
  const values = state.signInSignUpValues
    ? { ...state.signInSignUpValues }
    : { ...state.customizeValues };

  const validationValues = state.signInSignUpValidationState
    ? { ...state.signInSignUpValidationState }
    : { ...state.customizeValuesValidationState };

  if (!values.isAgreedToTermsAndConditions) {
    return {
      isValid: false,
      state: {
        hasPaymentBeenSubmitted: false,
        unknownError: 'Please agree to terms and conditions',
      },
    };
  }
  if (
    !values.email ||
    !validationValues.email ||
    !values.phoneNumber ||
    !validationValues.phoneNumber
  ) {
    return {
      isValid: false,
      state: { hasPaymentBeenSubmitted: false },
    };
  }
  return { isValid: true };
}

function formatKeyValues(prefix, objectToFormat) {
  return Object.keys(objectToFormat).reduce((previous, current) => {
    const formattedKeyname = `${prefix}${current
      .charAt(0)
      .toUpperCase()}${current.slice(1)}`;
    const previousKeyValue = previous;
    previousKeyValue[formattedKeyname] = objectToFormat[current];
    return previousKeyValue;
  }, {});
}

function flattenPaymentState(state) {
  const data = {
    giftMessage: state.giftMessage,
    shippingOption: state.selectedShippingOptionId,
    gender: state.babyGender || null,
    birthDay: state.babyBirthday?.day,
    birthMonth: state.babyBirthday?.month,
    birthYear: state.babyBirthday?.year,
    couponCode: state.cartInfo.coupon ? state.cartInfo.coupon.code : null,
    giftCards: '',
    checkoutVersion: state.checkoutVersion,
    isSubscribedToNewsletter: state.isSubscribedToNewsletter,
  };

  let formattedBillingValues;
  if (state.isBillingSameAsShipping) {
    formattedBillingValues = formatKeyValues('billing', state.shippingValues);
  } else {
    formattedBillingValues = formatKeyValues('billing', state.billingValues);
  }

  state.giftCards.forEach((card) => {
    data.giftCards += `${card.code}/`;
  });
  Object.assign(data, formattedBillingValues);
  Object.assign(data, formatKeyValues('shipping', state.shippingValues));
  return data;
}

async function runFacebookInit(data) {
  let billingAddress = data.billingValues;
  let name = {
    first: billingAddress.firstName,
    last: billingAddress.lastName,
  };

  const values = data.signInSignUpValues
    ? { ...data.signInSignUpValues }
    : { ...data.customizeValues };

  const { email, phoneNumber } = values;

  if (data.isBillingSameAsShipping) {
    billingAddress = data.shippingValues;
    name = {
      first: billingAddress.firstName,
      last: billingAddress.lastName,
    };
  }

  const facebookData = {
    billingAddress,
    email,
    phoneNumber,
    name,
  };

  if (data.cartInfo?.userId) {
    facebookData.userId = data.cartInfo.userId;
  }

  BBAnalytics.addAdvancedMatchingDataToMetaPixel(facebookData);
}

async function handleGuestCheckout(state) {
  const first = state.isBillingSameAsShipping
    ? state.shippingValues.firstName
    : state.billingValues.firstName;
  const last = state.isBillingSameAsShipping
    ? state.shippingValues.lastName
    : state.billingValues.lastName;

  const values = state.signInSignUpValues
    ? { ...state.signInSignUpValues }
    : { ...state.customizeValues };
  const guestCheckoutData = {
    firstName: first,
    lastName: last,
    guestCheckoutEmail: values.email,
    guestCheckoutPhoneNumber: values.phoneNumber,
    isSubscribedToNewsletter: values.isSubscribedToNewsletter,
    isAgreedToTermsAndConditions: values.isAgreedToTermsAndConditions,
  };

  if (!state.companyCode) {
    await runFacebookInit(state);
  }

  BBAnalytics.newUserEvent(
    {
      email: values.email,
      name: {
        first,
        last,
      },
      isSubscribedToNewsletter: values.isSubscribedToNewsletter,
    },
    'Checkout',
  );

  await BitsyApollo.addGuestCheckoutData(guestCheckoutData)
    .then((res) => {
      if (res.error) {
        return {
          unknownError: `Oops, something went wrong! If you continue to encounter this error, please contact our customer experience team at 888-913-7879 or ${Content.names.helpEmail}!`,
          isLoading: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      return null;
    })
    .catch((err) => ({
      isLoading: false,
      unknownError: err,
      hasPaymentBeenSubmitted: false,
    }));
}

/**
 *
 * @param {*} specialErrorMessage
 * @param {*} token
 * @param {*} paymentType
 * @param {*} state
 * @param {*} thisProps
 * @param {*} isGuestCheckout
 * @param {*} isLoadingFlag - this is sort of a hack around if you have a container that has a isLoading state that could get clobbered by the sideeffect of another component also doing things with isLoading. This could probably be refactored out with some better component hierarchy
 * @returns
 */
const paidPumpPurchaseState = {
  paidPumpPurchaseId: null,
};
const getPaidPumpPath = (state, purchaseParams, res) => {
  const { breastPumpChoices, breastPumpData, pageDisplayed } = state;
  const { isDmeUpsellPurchase } = purchaseParams;

  const selectedPump = breastPumpChoices?.find(
    (pump) => pump._id === breastPumpData?.breastPump,
  );
  const isPaidPump = selectedPump?.actualCost > 0;
  const isPaidPumpBeforeDmeUpsell =
    pageDisplayed === 'choose-breast-pump' &&
    isPaidPump &&
    !isDmeUpsellPurchase;
  if (isPaidPumpBeforeDmeUpsell) {
    window.insuranceCoveredPumpPurchaseId = res.justPurchasedPurchaseId;
    paidPumpPurchaseState.paidPumpPurchaseId = res.justPurchasedPurchaseId;
  }

  const isFeatureGateOn = Features.checkGate('dme_paid_pump_addons');
  const purchaseId =
    isPaidPump && isDmeUpsellPurchase && isFeatureGateOn
      ? paidPumpPurchaseState.paidPumpPurchaseId
      : res.justPurchasedPurchaseId;

  return isPaidPumpBeforeDmeUpsell && isFeatureGateOn
    ? generalHelpers.appendStoredUTMs(`/free-breast-pump/paid-pump-post-purchase`)
    : generalHelpers.appendStoredUTMs(`/purchase/thank-you/${purchaseId}`);
};

async function handlePurchaseCart(
  state,
  thisProps,
  purchaseParams,
  isLoadingFlag = 'isLoading',
  isPremiumUpgradeSelectedBeforeWelcomeMat = false,
  isPremiumUpgradeBeforeWelcomeMat = false,
) {
  const paymentData = flattenPaymentState(state);
  const { companyCode, companyInfo } = state;
  paymentData.paymentToken = purchaseParams.token
    ? purchaseParams.token.id
    : 'fakeToken';
  paymentData.isGift = false;
  paymentData.paymentType = purchaseParams.paymentType;
  paymentData.isPendingPayment = !companyCode;
  paymentData.companyCode = companyCode;
  paymentData.companyPaymentType = companyInfo?.paymentType || null;
  paymentData.isAnonymous = state.customizeValues
    ? state.customizeValues.isAnonymous
    : false;
  paymentData.registryId = thisProps.registryId;
  if (
    purchaseParams.isDmeUpsellPurchase &&
    window.insuranceCoveredPumpPurchaseId
  ) {
    paymentData.insuranceCoveredPumpPurchaseId =
      window.insuranceCoveredPumpPurchaseId;
    delete window.insuranceCoveredPumpPurchaseId;
  }
  const res = await CartUtils.purchaseCart(
    paymentData,
    purchaseParams.specialErrorMessage,
    isLoadingFlag,
  );

  if (res.unknownError || res.isError) {
    return { [isLoadingFlag]: false, isGuestCheckout: false, ...res };
  }
  const subTotal = MoneyUtils.formatAsMoney(res.subCosts.subTotal);
  const taxesAndFees = MoneyUtils.formatAsMoney(res.subCosts.taxesAndFees);
  const shipping = MoneyUtils.formatAsMoney(res.subCosts.shipping);
  const total = MoneyUtils.formatAsMoney(res.subCosts.overallTotal);

  const purchaseEventData = {
    res,
    subTotal,
    total,
    taxesAndFees,
    shipping,
    paymentType: purchaseParams.paymentType,
  };

  const userEmail =
    state.cartInfo?.purchaser?.email || state.signInSignUpValues?.email;

  console.log('cartinfo', state.cartInfo);

  BBAnalytics.purchaseEvent(purchaseEventData, state.cartInfo);
  BBAnalytics.setDueDateEvent(state.cartInfo?.babyBirthday, userEmail);

  localStorage.removeItem('tempCart');

  if (purchaseEventData?.res?.subscription != null) {
    const subscriptionPurchaseTraceEvent = {
      eventName: 'checkout2024_subscription_purchased',
      metadata: {
        /* state.checkoutArm, not paymentData.checkoutVersion: checkoutVersion
           is sent to the server, where truthiness — not just === '2024' —
           changes behavior (purchaseCartUtils.js:1248 flips
           checkoutFullyCompleted, purchase.directives.js:2381 reclassifies the
           purchase). checkoutArm is read here only; flattenPaymentState does
           not copy it, so it never reaches the mutation. */
        checkoutVersion:
          state.checkoutArm ||
          (paymentData.checkoutVersion === '2024' ? '2024' : 'legacy'),
        purchaseId: res.justPurchasedPurchaseId,
      },
    };

    Features.logEvent(subscriptionPurchaseTraceEvent);
  }

  // dont love this, but we need to get pathing out of DME-specific functions --tlb 101/24/2024
  let fullPath;
  if (paymentData.checkoutVersion === '2024') {
    fullPath = generalHelpers.appendStoredUTMs(`/checkout/welcome/${res.justPurchasedPurchaseId}`);
  } else {
    fullPath = getPaidPumpPath(state, purchaseParams, res);
  }

  // Split the full path into pathname and search for react-router
  const [pathname, search] = fullPath.split('?');
  
  thisProps.history.push({
    pathname,
    search: search ? `?${search}` : '',
    state: {
      paymentData,
      email: userEmail,
      companyCode,
      companyInfo,
      registryId: thisProps.registryId,
      shareCode: thisProps.shareCode,
      purchaseId: res.justPurchasedPurchaseId,
      res,
      isPremiumUpgradeSelectedBeforeWelcomeMat,
      isPremiumUpgradeBeforeWelcomeMat,
    },
  });
  thisProps.onCartUpdate();
  return { isGuestCheckout: false };
}

/**
 *
 * @param {*} props
 * @param {*} specialErrorMessage
 * @param {*} state
 * @param {*} thisProps
 * @param {*} isGuestCheckout
 * @param {*} isLoadingFlag - this is sort of a hack around if you have a container that has a isLoading state that could get clobbered by the sideeffect of another component also doing things with isLoading. This could probably be refactored out with some better component hierarchy
 * @param {*} isPremiumUpgradeSelectedBeforeWelcomeMat
 * @returns
 */
async function handleStripeToken(
  props,
  specialErrorMessage,
  state,
  thisProps,
  isGuestCheckout,
  isDmeUpsellPurchase,
  isLoadingFlag = 'isLoading',
  isPremiumUpgradeSelectedBeforeWelcomeMat = false,
  isPremiumUpgradeBeforeWelcomeMat = false,
) {
  let fakeToken = isDmeUpsellPurchase ? 'DME Addon Purchase' : '';

  if (
    state.userInfo &&
    state.userInfo.creditCards.length &&
    state.usePreviousCard
  ) {
    fakeToken = 'Use Previous Card';
    BBAnalytics.usePreviousCard();
  }

  const purchaseParams = {
    specialErrorMessage,
    isGuestCheckout,
    isDmeUpsellPurchase,
    fakeToken,
    paymentType: isDmeUpsellPurchase
      ? 'DME Addon Purchase'
      : 'Credit Card Form',
  };

  if (fakeToken) {
    const result = await handlePurchaseCart(
      state,
      thisProps,
      purchaseParams,
      isLoadingFlag,
      isPremiumUpgradeSelectedBeforeWelcomeMat,
      isPremiumUpgradeBeforeWelcomeMat,
    );

    if (result.unknownError || result.isError || result.paymentError) {
      Logger.error(result.unknownError || result.isError || result.paymentError, {
        errorType: result.paymentError ? 'PAYMENT_ERROR' : 'PURCHASE_CART_ERROR',
        context: 'handleStripeToken - fakeToken path',
        paymentType: purchaseParams.paymentType,
        result
      });
    }
    
    return result;
  }

  const first = state.isBillingSameAsShipping
    ? state.shippingValues.firstName
    : state.billingValues.firstName;
  const last = state.isBillingSameAsShipping
    ? state.shippingValues.lastName
    : state.billingValues.lastName;

  return props.stripe
    .createToken({
      name: `${first} ${last}`,
    })
    .then(async ({ token }) => {
      if (token && token.id) {
        purchaseParams.token = token;

        const result = await handlePurchaseCart(
          state,
          thisProps,
          purchaseParams,
          isLoadingFlag,
          isPremiumUpgradeSelectedBeforeWelcomeMat,
          isPremiumUpgradeBeforeWelcomeMat,
        );

        if (result.unknownError || result.isError || result.paymentError) {
          Logger.error(result.unknownError || result.isError || result.paymentError, {
            errorType: result.paymentError ? 'PAYMENT_ERROR' : 'PURCHASE_CART_ERROR',
            context: 'handleStripeToken - stripe token path',
            paymentType: purchaseParams.paymentType,
            result
          });
        }
        
        return result;
      }
      if (!state.creditCardError) {
        return {
          unknownError: 'Something went wrong, please try again',
          [isLoadingFlag]: false,
          isGuestCheckout: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      if (specialErrorMessage === 'logged') {
        return {
          unknownError:
            "Oops! We've logged you in but something is wrong with your credit card info, please see below.",
          [isLoadingFlag]: false,
          isGuestCheckout: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      if (specialErrorMessage === 'guest') {
        return {
          unknownError:
            "Oops! We've stored your email and phone number but something is wrong with your credit card info, please see below.",
          [isLoadingFlag]: false,
          isGuestCheckout: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      return {
        unknownError:
          'Oops! Something is wrong with your credit card info, please see below.',
        [isLoadingFlag]: false,
        isGuestCheckout: false,
        hasPaymentBeenSubmitted: false,
      };
    })
    .catch((err) => {
      Logger.error(err);
      if (!state.creditCardError) {
        return {
          unknownError: 'Something went wrong, please try again',
          [isLoadingFlag]: false,
          isGuestCheckout: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      if (specialErrorMessage === 'logged') {
        return {
          unknownError:
            "Oops! We've logged you in but something is wrong with your credit card info, please see below.",
          [isLoadingFlag]: false,
          isGuestCheckout: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      if (specialErrorMessage === 'guest') {
        return {
          unknownError:
            "Oops! We've stored your email and phone number but something is wrong with your credit card info, please see below.",
          [isLoadingFlag]: false,
          isGuestCheckout: false,
          hasPaymentBeenSubmitted: false,
        };
      }
      return {
        unknownError:
          'Oops! Something is wrong with your credit card info, please see below.',
        [isLoadingFlag]: false,
        isGuestCheckout: false,
        hasPaymentBeenSubmitted: false,
      };
    });
}

function determineIfGiftboxOnlyPurchase(cart) {
  if (
    !cart ||
    !cart.productsAndDetails ||
    !cart.productsAndDetails.length ||
    cart.subscription
  ) {
    return false;
  }

  const numberOfItemsInCart = cart.productsAndDetails.length;
  const numberOfGiftBoxesInCart = cart.productsAndDetails.filter((prodObj) => {
    const { tags } = prodObj.product;
    if (tags && tags.length) {
      const isGiftBox = tags.some((tag) => tag.description === 'Gift Boxes');
      if (isGiftBox) {
        return prodObj;
      }
    }
    return null;
  }).length;
  return numberOfGiftBoxesInCart === numberOfItemsInCart;
}

async function sendSMSOptIn(
  isSmsOptInSelected,
  email,
  phoneNumber,
  isSubscribedToNewsletter,
  name,
  source,
) {
  // send SMS Opt In to Attentive if isSmsOptInSelected,
  // has the correct data needed or is logged in and the
  // data will be pulled from the user
  if (isSmsOptInSelected && ((email && phoneNumber) || Auth.isLoggedIn())) {
    const smsOptInData = {
      isOptedInSMS: true,
      email,
      phoneNumber,
      isSubscribedToNewsletter,
      provider: Environment.store,
      source,
    };
    if (name) {
      smsOptInData.firstName = name.firstName;
      smsOptInData.lastName = name.lastName;
    }
    await BitsyApollo.storeAndSendSMSOptIn(smsOptInData);
    BBAnalytics.optedIntoSMS().catch((err) => Logger.error(err));
  }
}

export default {
  getAppropriateShippingOption,
  getDefaultExclusiveShipping,
  calculateOrderDerivedValues,
  getFilteredShippingOptions,
  calculateApplicablePoints,
  getAvailablePoints,
  updateGuestCheckoutFormValidity,
  handleGuestCheckout,
  handleStripeToken,
  handlePurchaseCart,
  determineIfGiftboxOnlyPurchase,
  sendSMSOptIn,
  runFacebookInit,
};
